import React, { useMemo, useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";

/**
 * Code 128 visual barcode rendered into a canvas (same encoding banks use).
 */
function BarcodeVisual({ data = "" }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current || !data) return;
    try {
      JsBarcode(ref.current, data, {
        format: "CODE128C",
        displayValue: false,
        width: 1.3,
        height: 50,
        margin: 0,
      });
    } catch {
      try {
        JsBarcode(ref.current, data, {
          format: "CODE128",
          displayValue: false,
          width: 1.3,
          height: 50,
          margin: 0,
        });
      } catch {
        /* ignore */
      }
    }
  }, [data]);
  return <canvas ref={ref} style={{ width: "100%", maxWidth: 360, height: 50 }} />;
}

const fmtDate = (d) => (d ? d.split("-").reverse().join(".") : "");
const fmt = (n) => (Number(n) || 0).toFixed(2).replace(".", ",") + " \u20AC";

/**
 * Suomalainen vakiopohjainen laskun esikatselu (ylä = lasku, ala = tilisiirto).
 */
export default function InvoicePreview({ form, subtotal, vatTotal, totalAmount, barcodeData, settings }) {
  const vatBreakdown = useMemo(() => {
    const groups = {};
    (form.items || []).forEach((item) => {
      const pct = Number(item.vat_percent) || 0;
      if (!groups[pct]) groups[pct] = { net: 0, vat: 0 };
      const net = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
      groups[pct].net += net;
      groups[pct].vat += net * (pct / 100);
    });
    return Object.entries(groups).sort(([a], [b]) => parseFloat(b) - parseFloat(a));
  }, [form.items]);

  const logoSettings = settings || {};

  return (
    <div
      id="invoice-print-area"
      style={{
        fontFamily: "Arial, Helvetica, sans-serif",
        fontSize: 11,
        color: "#000",
        background: "#fff",
        width: "100%",
        maxWidth: 794,
        margin: "0 auto",
        position: "relative",
        boxSizing: "border-box",
      }}
      data-testid="invoice-preview"
    >
      {logoSettings.logo_url && logoSettings.logo_position === "background" && (
        <img
          src={logoSettings.logo_url}
          alt=""
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: logoSettings.logo_width || 200,
            height: logoSettings.logo_height || 80,
            objectFit: "contain",
            opacity: (logoSettings.logo_opacity || 15) / 100,
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
      )}

      <div style={{ padding: "28px 32px 0 32px", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            {logoSettings.logo_url && logoSettings.logo_position === "corner" && (
              <img
                src={logoSettings.logo_url}
                alt="Logo"
                style={{
                  width: (logoSettings.logo_width || 120) / 1.5,
                  height: (logoSettings.logo_height || 60) / 1.5,
                  objectFit: "contain",
                }}
              />
            )}
            <div>
              <div style={{ fontWeight: "bold", fontSize: 13 }}>{form.sender_name}</div>
              <div>{form.sender_address}</div>
              {form.sender_business_id && <div>Y-tunnus {form.sender_business_id}</div>}
            </div>
          </div>

          <div style={{ textAlign: "left", minWidth: 220 }}>
            <div style={{ fontWeight: "bold", fontSize: 18, letterSpacing: 1, marginBottom: 6 }}>LASKU</div>
            <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
              <tbody>
                <tr>
                  <td style={{ paddingRight: 16, color: "#444" }}>Päivämäärä</td>
                  <td style={{ fontWeight: 500 }}>{fmtDate(form.invoice_date)}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 16, color: "#444" }}>Laskun numero</td>
                  <td style={{ fontWeight: 500 }}>{form.invoice_number}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 16, color: "#444" }}>Viitenumero</td>
                  <td style={{ fontWeight: 500 }}>{form.reference_number}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 16, color: "#444" }}>Maksuehdot</td>
                  <td>{form.payment_terms || form.payment_terms_days || 14} pv netto</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 16, color: "#444" }}>Eräpäivä</td>
                  <td style={{ fontWeight: 500 }}>{fmtDate(form.due_date)}</td>
                </tr>
                <tr>
                  <td style={{ paddingRight: 16, color: "#444" }}>Viivästyskorko</td>
                  <td>{form.penalty_interest ?? 7} %</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ display: "flex", gap: 40, fontSize: 10, color: "#444", marginBottom: 16 }}>
          {form.sender_phone && <span>Puh. {form.sender_phone}</span>}
          {form.sender_email && <span>Sähköposti {form.sender_email}</span>}
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: "bold", fontSize: 12 }}>{form.recipient_name}</div>
          {form.recipient_business_id && (
            <div style={{ fontSize: 10, color: "#555" }}>Y-tunnus {form.recipient_business_id}</div>
          )}
          <div style={{ fontSize: 11 }}>{form.recipient_address}</div>
          {form.recipient_email && <div style={{ fontSize: 10, color: "#555" }}>{form.recipient_email}</div>}
        </div>

        <div style={{ borderTop: "2px solid #000", marginBottom: 0 }} />

        <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 0, fontSize: 11 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid #ccc" }}>
              <th style={{ textAlign: "left", padding: "6px 4px", fontWeight: 600 }}>Nimi</th>
              <th style={{ textAlign: "right", padding: "6px 4px", fontWeight: 600 }}>Määrä</th>
              <th style={{ textAlign: "right", padding: "6px 4px", fontWeight: 600 }}>à-hinta €</th>
              <th style={{ textAlign: "right", padding: "6px 4px", fontWeight: 600 }}>Alv-%</th>
              <th style={{ textAlign: "right", padding: "6px 4px", fontWeight: 600 }}>Yhteishinta €</th>
            </tr>
          </thead>
          <tbody>
            {(form.items || []).map((item, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #eee" }}>
                <td style={{ padding: "5px 4px" }}>{item.description}</td>
                <td style={{ textAlign: "right", padding: "5px 4px" }}>
                  {item.quantity} {item.unit}
                </td>
                <td style={{ textAlign: "right", padding: "5px 4px" }}>
                  {(Number(item.unit_price) || 0).toFixed(2).replace(".", ",")} €
                </td>
                <td style={{ textAlign: "right", padding: "5px 4px" }}>{item.vat_percent} %</td>
                <td style={{ textAlign: "right", padding: "5px 4px", fontWeight: 500 }}>
                  {(Number(item.total) || 0).toFixed(2).replace(".", ",")} €
                </td>
              </tr>
            ))}
            {(form.items || []).length < 8 &&
              Array.from({ length: Math.max(0, 5 - (form.items || []).length) }).map((_, i) => (
                <tr key={`empty-${i}`}>
                  <td colSpan={5} style={{ padding: "5px 4px", height: 22 }}>
                    &nbsp;
                  </td>
                </tr>
              ))}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <table style={{ borderCollapse: "collapse", fontSize: 11, minWidth: 320 }}>
            <tbody>
              {vatBreakdown.map(([pct, data]) => (
                <tr key={pct} style={{ color: "#555" }}>
                  <td style={{ textAlign: "left", paddingRight: 20 }}>Veroerittely ALV {pct} %</td>
                  <td style={{ textAlign: "right" }}>
                    {data.net.toFixed(2).replace(".", ",")} € + ALV {data.vat.toFixed(2).replace(".", ",")} €
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: "1px solid #ccc" }}>
                <td style={{ textAlign: "left", paddingRight: 20, paddingTop: 4 }}>Arvonlisäveroton hinta €</td>
                <td style={{ textAlign: "right", paddingTop: 4, fontWeight: 600 }}>
                  {(Number(subtotal) || 0).toFixed(2).replace(".", ",")} €
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: "left", paddingRight: 20 }}>Arvonlisävero yhteensä €</td>
                <td style={{ textAlign: "right", fontWeight: 600 }}>
                  {(Number(vatTotal) || 0).toFixed(2).replace(".", ",")} €
                </td>
              </tr>
              <tr style={{ borderTop: "2px solid #000" }}>
                <td style={{ textAlign: "left", paddingRight: 20, paddingTop: 4, fontWeight: "bold", fontSize: 12 }}>
                  Maksettava €
                </td>
                <td
                  style={{
                    textAlign: "right",
                    paddingTop: 4,
                    fontWeight: "bold",
                    fontSize: 13,
                    textDecoration: "underline",
                  }}
                >
                  {(Number(totalAmount) || 0).toFixed(2).replace(".", ",")} €
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {form.notes && (
          <div style={{ marginTop: 12, fontSize: 10, color: "#555", borderTop: "1px solid #eee", paddingTop: 8 }}>
            {form.notes}
          </div>
        )}
      </div>

      {/* TILISIIRTO */}
      <div style={{ marginTop: 24, borderTop: "3px solid #000", position: "relative", zIndex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #ccc", padding: "8px 32px" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 9, color: "#666", textTransform: "uppercase" }}>Eräpäivä</div>
            <div style={{ fontWeight: "bold", fontSize: 14 }}>{fmtDate(form.due_date)}</div>
          </div>
          <div style={{ flex: 2 }}>
            <div style={{ fontSize: 9, color: "#666", textTransform: "uppercase" }}>Viitenumero</div>
            <div style={{ fontWeight: "bold", fontSize: 14, letterSpacing: 2 }}>{form.reference_number}</div>
          </div>
          <div style={{ flex: 1, textAlign: "right" }}>
            <div style={{ fontSize: 9, color: "#666" }}>Yhteensä</div>
            <div style={{ fontWeight: "bold", fontSize: 16 }}>{fmt(totalAmount)}</div>
          </div>
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid #ccc" }}>
          <div style={{ flex: 1, padding: "8px 32px", borderRight: "1px solid #ccc" }}>
            <div style={{ fontSize: 9, color: "#666", textTransform: "uppercase", marginBottom: 2 }}>BIC / SWIFT</div>
            <div style={{ fontWeight: "bold", fontSize: 13, letterSpacing: 1 }}>
              {(settings?.bic || (form.sender_bank_account?.startsWith("FI") ? "NDEAFIHH" : ""))}
            </div>
          </div>
          <div style={{ flex: 2, padding: "8px 32px", borderRight: "1px solid #ccc" }}>
            <div style={{ fontSize: 9, color: "#666", textTransform: "uppercase", marginBottom: 2 }}>IBAN</div>
            <div style={{ fontWeight: "bold", fontSize: 13, letterSpacing: 0.5 }}>
              {form.sender_bank_account || settings?.bank_account || ""}
            </div>
          </div>
          <div style={{ flex: 2, padding: "8px 32px", fontSize: 10 }}>
            <div style={{ fontWeight: "bold" }}>{form.sender_name}</div>
            <div style={{ color: "#555" }}>{form.sender_address}</div>
            {form.sender_phone && <div style={{ color: "#555" }}>Puh: {form.sender_phone}</div>}
            {form.sender_email && <div style={{ color: "#555" }}>{form.sender_email}</div>}
            {form.sender_business_id && <div style={{ color: "#555" }}>Y-tunnus {form.sender_business_id}</div>}
          </div>
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid #ccc" }}>
          <div style={{ width: 100, padding: "6px 8px 6px 32px", fontSize: 9, color: "#666", lineHeight: 1.4 }}>
            Saaja
            <br />
            Mottagare
          </div>
          <div style={{ flex: 1, padding: "6px 8px", fontSize: 11, fontWeight: 500 }}>
            <div>{form.sender_name}</div>
            <div>{form.sender_address}</div>
          </div>
          <div style={{ width: 180, padding: "6px 32px 6px 8px", fontSize: 11 }}>
            <div style={{ fontSize: 9, color: "#666", marginBottom: 2 }}>Allekirjoitus / Underskrift</div>
            <div style={{ borderBottom: "1px solid #000", height: 24 }}></div>
          </div>
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid #ccc" }}>
          <div style={{ width: 100, padding: "6px 8px 6px 32px", fontSize: 9, color: "#666", lineHeight: 1.4 }}>
            Maksajan nimi
            <br />
            ja osoite
            <br />
            Betalarens
            <br />
            namn och
            <br />
            adress
          </div>
          <div style={{ flex: 1, padding: "6px 8px", fontSize: 11 }}>
            <div style={{ fontWeight: 500 }}>{form.recipient_name}</div>
            <div>{form.recipient_address}</div>
          </div>
          <div style={{ width: 180, padding: "6px 32px 6px 8px", fontSize: 10, color: "#444" }}>
            <div style={{ fontSize: 9, color: "#666", marginBottom: 2 }}>Tilitä nro / Från konto nr</div>
            <div style={{ borderBottom: "1px solid #ccc", height: 20, marginBottom: 6 }}></div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", padding: "12px 32px", gap: 24 }}>
          <div style={{ flex: 1 }}>
            <BarcodeVisual data={barcodeData} />
            <div style={{ fontSize: 8, color: "#888", marginTop: 3, letterSpacing: 0.5, fontFamily: "monospace" }}>
              {barcodeData ? barcodeData.substring(0, 54) : ""}
            </div>
          </div>
          <div style={{ maxWidth: 220, fontSize: 9, color: "#555", lineHeight: 1.5 }}>
            Maksu välitetään saajalle maksujenvälityksen ehtojen mukaisesti ja vain maksajan ilmoittaman tilinumeron
            perusteella. Betalningen förmedlas till mottagaren enligt villkoren för betalningstjänster och endast till
            det kontonummer som betalaren angett.
          </div>
        </div>
      </div>
    </div>
  );
}
